import type { IndustryPage } from '../industries';

const lines = [
  { item: 'Fall cleanup: leaves blown and mulched from lawn and beds, about 9,000 sq ft lot, hauled away', amount: '$385' },
  { item: 'Core aeration, 6,500 sq ft of turf, two directions on the thin side yard', amount: '$165' },
  { item: 'Overseed with tall fescue blend after aeration, starter fertilizer included', amount: '$210' },
  { item: 'Optional: edge and refresh mulch in front beds, 3 cubic yards', amount: '$295' },
];

const faq = [
  {
    q: 'Can Scopie handle my weekly mowing route?',
    a: 'Not today. Scopie has no recurring visits, route planning or automatic monthly billing yet. It works well for the quoted side of lawn care: cleanups, aeration and overseeding, sod, mulch and bed work, and other one-off or seasonal jobs.',
  },
  {
    q: 'How should I price aeration and overseeding?',
    a: 'Usually by the square footage of turf, with seed and fertilizer either included or listed as material. Measure the lawn rather than the lot, and say which areas are excluded, such as slopes or beds.',
  },
  {
    q: 'Can a customer add mulch or bed edging on the quote?',
    a: 'Yes. Put it on the quote as an optional line. The customer ticks it when they open the link, and the total changes before they sign.',
  },
  {
    q: 'Can I email quotes to my whole customer list in the fall?',
    a: 'Scopie emails each quote to its customer when you send it. Customers stay in your client list with last year\'s work, so preparing a round of fall cleanup quotes starts from what you did for each of them before.',
  },
];

const page: IndustryPage = {
  slug: 'lawn-care',
  order: 18,
  group: 'Outdoor',
  path: '/for/lawn-care',
  primaryKeyword: 'lawn care business software',
  secondaryKeywords: ['lawn care quote template', 'lawn aeration quote', 'fall cleanup pricing'],
  title: 'Lawn Care Software for Cleanups and Aeration | Scopie', // 52
  metaDescription:
    'Quote fall cleanups, aeration, overseeding and mulch by the square foot, let customers add extras with a tick, and email every seasonal quote in minutes.', // 154
  h1: 'Lawn care quotes for the jobs that are not weekly mowing',
  answerFirst:
    'Scopie is for the quoted side of a lawn care business: spring and fall cleanups, aeration and overseeding, sod patches, mulch and bed work. Price turf by the square foot, offer mulch as a tick-box extra, and email the quote. Weekly mowing routes and recurring billing are not built yet, and this page says so.',
  exampleQuote: { label: 'Sample fall lawn quote', lines, total: '$1,055', depositNote: 'Weather and scheduling terms are the company\'s own clauses.' },
  blocks: [
    {
      type: 'text',
      id: 'two-businesses',
      h2: 'Most lawn companies run two businesses',
      lead: 'Mowing pays the bills. The cleanups, aeration and mulch are where the margin is.',
      paragraphs: [
        'Weekly mowing is a route: same houses, same day, same price. The seasonal work is different. Every cleanup and every aeration is its own job with its own size and price, and customers want to see a quote before they agree to it.',
        'Scopie is built for that second business. If your mowing route runs in a separate tool or a notebook, use Scopie to quote and invoice the seasonal work on top of it.',
      ],
    },
    {
      type: 'list',
      id: 'calendar',
      h2: 'The lawn care quoting calendar',
      style: 'bullets',
      items: [
        { title: 'March to April', text: 'Spring cleanups, dethatching, pre-emergent, bed edging and the first mulch orders.' },
        { title: 'May to June', text: 'Sod repairs, new beds and small planting jobs.' },
        { title: 'August to September', text: 'Core aeration and overseeding for cool-season grass; the best window for seed.' },
        { title: 'October to November', text: 'Fall cleanups and gutter-side leaf removal; many companies sell snow work at the same time.' },
      ],
    },
    {
      type: 'sample-quote',
      id: 'sample',
      h2: 'A fall package for one property',
      label: 'Sample quote: cleanup, aeration and overseed',
      lines,
      total: '$1,055',
      totalLabel: 'Total if the mulch refresh is ticked',
      note: 'Example numbers. The mulch line is optional; the customer decides on the quote page.',
    },
    {
      type: 'list',
      id: 'mistakes',
      h2: 'Mistakes on seasonal lawn quotes',
      style: 'mistakes',
      items: [
        { title: 'Pricing the lot, not the turf', text: 'A big lot with a big house has less lawn than it looks. Measure the grass.' },
        { title: 'Leaf volume guessed in September', text: 'Quote cleanups when the trees are visible, or state how many visits or loads are included.' },
        { title: 'Seed results promised', text: 'Germination depends on watering. Say who waters and that results vary.' },
        { title: 'Haul-away forgotten', text: 'Dumping fees and trailer trips add up. Put disposal on the quote.' },
        { title: 'Mulch depth and color left open', text: 'Two inches of brown versus three of black is a real price difference.' },
      ],
    },
    {
      type: 'text',
      id: 'after',
      h2: 'Crews, equipment and invoices',
      paragraphs: [
        'Signed jobs go on the schedule with the crew and the equipment, such as the aerator or the leaf loader, so the team can see which machine is out on which day. Crew members see their addresses and notes, not the prices. When the job is done, Scopie emails the invoice link to the customer.',
      ],
    },
    { type: 'faq', id: 'faq', h2: 'Lawn care quoting questions', items: faq },
  ],
  sections: [],
  faq,
  internalLinks: [
    { href: '/for', anchor: 'All industries', status: 'live' },
    { href: '/pricing', anchor: 'Scopie plans and pricing', status: 'live' },
    { href: '/for/landscaping', anchor: 'Scopie for landscaping businesses', status: 'live' },
    { href: '/for/snow-removal', anchor: 'Scopie for snow removal', status: 'live' },
    { href: '/templates/landscaping-quote-template', anchor: 'landscaping quote template', status: 'live' },
  ],
  cta: {
    heading: 'Quote the fall rush in an afternoon',
    text: 'Save your cleanup, aeration and mulch prices in Scopie, and send each seasonal quote from the truck.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
  claimsToVerify: [],
};

export default page;
